import { X, Check } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const ROWS = [
  ["Mostly tenant-focused", "Owner-focused"],
  ["Limited accountability", "Structured service"],
  ["Phone updates", "Photo/video reporting"],
  ["Multiple vendors", "Single point of contact"],
  ["Informal billing", "Transparent billing"],
  ["Reactive maintenance", "Planned property care"],
  ["Difficult to track history", "Property records"],
  ["Personal dependency", "Professional process"],
  ["Limited documentation", "Digital documentation"],
  ["Owner must coordinate", "We coordinate"],
];

const PRINCIPLES = [
  { n: "01", title: "Transparency", copy: "Clear scope, clear charges and clear reporting." },
  { n: "02", title: "Owner Approval", copy: "Major expenses and works require owner approval wherever practical." },
  { n: "03", title: "Proof of Work", copy: "Photos, videos, invoices and completion updates wherever applicable." },
];

export const Comparison = () => (
  <section className="border-y border-navy-900/10 bg-white py-24 sm:py-32" data-testid="comparison-section">
    <div className="container-site">
      <SectionHeading
        chapter="09"
        eyebrow="Why NRI Property Services?"
        title="Your Property. Your Decisions. Our Execution."
        copy="The difference between an informal contact and a professional local presence."
      />

      <Reveal delay={0.1}>
        <div className="mt-14 grid gap-4 lg:grid-cols-2" data-testid="comparison-table">
          <div className="rounded-2xl border border-navy-900/10 bg-ivory p-7 sm:p-9">
            <h3 className="font-display text-sm font-extrabold uppercase tracking-[0.22em] text-navy-900/50">
              Traditional Broker / Informal Contact
            </h3>
            <ul className="mt-7 space-y-4">
              {ROWS.map(([bad]) => (
                <li key={bad} className="flex items-center gap-3 text-sm text-navy-900/55">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy-900/5">
                    <X className="h-3.5 w-3.5 text-navy-900/40" />
                  </span>
                  {bad}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-navy-900 p-7 shadow-[0_30px_60px_-25px_rgba(10,17,40,0.5)] sm:p-9">
            <h3 className="font-display text-sm font-extrabold uppercase tracking-[0.22em] text-gold-400">
              NRI Property Services
            </h3>
            <ul className="mt-7 space-y-4">
              {ROWS.map(([, good]) => (
                <li key={good} className="flex items-center gap-3 text-sm font-medium text-white">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold-500/15">
                    <Check className="h-3.5 w-3.5 text-gold-400" />
                  </span>
                  {good}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <Stagger className="mt-16 grid gap-4 sm:grid-cols-3" gap={0.1}>
        {PRINCIPLES.map((p) => (
          <StaggerItem key={p.n}>
            <div className="h-full rounded-2xl border border-navy-900/10 bg-ivory p-7" data-testid={`principle-${p.n}`}>
              <span className="font-display text-3xl font-extrabold tracking-tight text-gold-500/80">{p.n}</span>
              <h3 className="mt-3 font-display text-lg font-extrabold tracking-tight text-navy-900">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.copy}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>

      <Reveal delay={0.15}>
        <p className="mt-12 text-center font-display text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
          We don&rsquo;t just say <span className="text-navy-900/40">&ldquo;it&rsquo;s done.&rdquo;</span>{" "}
          <span className="text-gold-600">We show you.</span>
        </p>
      </Reveal>
    </div>
  </section>
);
